// Missions — the narrative layer over beats (Phase 6). A mission is a short, themed
// Spidey story whose beats share a setting/arc, so a session reads as a story rather than
// random practice lines. Missions are a SKIN + a soft clustering hint; the spaced-
// repetition engine still decides which PATTERNS are due (see engine/session.ts).
//
// MISSIONS is the source of truth (ordered beatIds); a beat's mission is derived, so the
// two can't desync. A beat may belong to no mission (it's then picked individually).

import type { LevelId } from './sequence';

export interface Mission {
  id: string;
  /** Kid-facing briefing title, e.g. "Bridge Rescue". */
  title: string;
  level: LevelId;
  /** A short villain/setting tag for theming + art. */
  theme: string;
  /** Beat ids in narrative order. */
  beatIds: string[];
}

export const MISSIONS: Mission[] = [
  // ---- Chapter 1 · Green Goblin (CVC) ----
  { id: 'm_cvc_alley', title: 'The Back-Alley Bug', level: 'cvc', theme: 'goblin', beatIds: ['a1', 'a2', 'u1', 'u2'] },
  { id: 'm_cvc_rooftop', title: 'Rooftop Nap', level: 'cvc', theme: 'goblin', beatIds: ['e1', 'e2', 'i1', 'i2'] },
  { id: 'm_cvc_lab', title: 'Glider Mix-Up', level: 'cvc', theme: 'goblin', beatIds: ['o1', 'o2'] },
  // ---- Chapter 2 · Doc Ock (Digraphs) ----
  { id: 'm_dig_docks', title: 'Trouble at the Docks', level: 'digraphs', theme: 'ock', beatIds: ['sh1', 'sh2', 'ck1'] },
  { id: 'm_dig_chase', title: 'The Chip-Shop Chase', level: 'digraphs', theme: 'ock', beatIds: ['ch1', 'ch2', 'th1', 'th2'] },
  { id: 'm_dig_signs', title: 'Jumbled Lab Signs', level: 'digraphs', theme: 'ock', beatIds: ['wh1', 'wh2', 'ng1', 'ng2'] },
  { id: 'm_dig_arms', title: 'Eight Arms, One Plan', level: 'digraphs', theme: 'ock', beatIds: ['sh3', 'ch3', 'th3', 'ck2'] },
  // ---- Chapter 3 · Rhino (Blends) ----
  { id: 'm_bl_nest', title: 'Nest on the Sand', level: 'blends', theme: 'rhino', beatIds: ['st1', 'nd1'] },
  { id: 'm_bl_smash', title: 'Rhino on the Run', level: 'blends', theme: 'rhino', beatIds: ['bl1', 'bl2', 'cl1', 'cl2'] },
  { id: 'm_bl_street', title: 'Smash on the Street', level: 'blends', theme: 'rhino', beatIds: ['fl1', 'fl2', 'gr1', 'gr2'] },
  { id: 'm_bl_charge', title: 'The Big Charge', level: 'blends', theme: 'rhino', beatIds: ['tr1', 'tr2', 'st2', 'st3'] },
  { id: 'm_bl_stop', title: 'Stop That Rhino!', level: 'blends', theme: 'rhino', beatIds: ['nd2', 'nd3', 'mp1', 'mp2'] },
  // ---- Chapter 4 · Sandman (Silent-e) ----
  { id: 'm_vce_bakery', title: 'Cake & a Bike', level: 'vce', theme: 'sandman', beatIds: ['ae1', 'ie1'] },
  { id: 'm_vce_beach', title: 'Sand on the Beach', level: 'vce', theme: 'sandman', beatIds: ['ae2', 'ae3', 'ae4', 'ie2'] },
  { id: 'm_vce_dunes', title: 'Over the Dunes', level: 'vce', theme: 'sandman', beatIds: ['ie3', 'ie4', 'oe1', 'oe2'] },
  { id: 'm_vce_tide', title: 'Beat the Tide', level: 'vce', theme: 'sandman', beatIds: ['oe3', 'oe4', 'ue1', 'ue2', 'ue3'] },
  // ---- Chapter 5 · Electro (Vowel teams) ----
  { id: 'm_vt_storm', title: 'Storm on the Bay', level: 'vowel_teams', theme: 'electro', beatIds: ['vt1', 'vt2', 'vt4'] },
  { id: 'm_vt_green', title: 'The Green Spark', level: 'vowel_teams', theme: 'electro', beatIds: ['vt3'] },
  { id: 'm_vt_surge', title: 'Power Surge', level: 'vowel_teams', theme: 'electro', beatIds: ['vt5', 'vt6', 'vt7'] },
  { id: 'm_vt_grid', title: 'Save the Grid', level: 'vowel_teams', theme: 'electro', beatIds: ['vt8', 'vt9'] },
  // ---- Chapter 6 · Vulture (R-controlled) ----
  { id: 'm_rc_chase', title: 'Car Chase', level: 'r_controlled', theme: 'vulture', beatIds: ['rc1', 'rc2', 'rc3'] },
  { id: 'm_rc_sky', title: 'Up in the Sky', level: 'r_controlled', theme: 'vulture', beatIds: ['rc4', 'rc5', 'rc6'] },
  { id: 'm_rc_swoop', title: 'The Big Swoop', level: 'r_controlled', theme: 'vulture', beatIds: ['rc7', 'rc8'] },
  // ---- Chapter 7 · Venom (Diphthongs — finale) ----
  { id: 'm_di_night', title: 'Moonlit Patrol', level: 'diphthongs', theme: 'venom', beatIds: ['di1', 'di2', 'di3', 'di4'] },
  { id: 'm_di_downtown', title: 'Downtown Showdown', level: 'diphthongs', theme: 'venom', beatIds: ['di5', 'di6', 'di7'] },
  { id: 'm_di_final', title: 'The Final Stand', level: 'diphthongs', theme: 'venom', beatIds: ['di8', 'di9', 'di10'] },
];

const BY_BEAT = new Map<string, Mission>();
for (const m of MISSIONS) for (const id of m.beatIds) if (!BY_BEAT.has(id)) BY_BEAT.set(id, m);

/** The mission a beat belongs to (undefined if it's a standalone beat). */
export function missionOf(beatId: string): Mission | undefined {
  return BY_BEAT.get(beatId);
}

export function missionById(id: string): Mission | undefined {
  return MISSIONS.find((m) => m.id === id);
}
